import { useEffect, useRef, useState } from 'react';
import { Loader2, RotateCcw, Upload } from 'lucide-react';
import { ToggleInput } from '@/Components/shared/ToggleInput';
import type { PresaleSheetConfig } from '../api/presaleSheetApi';
import { DEFAULT_HEADER_IMAGE } from '../constants';
import {
  useDeletePresaleHeaderImage,
  useUploadPresaleHeaderImage,
  useUpsertPresaleSheetConfig,
} from '../hooks/usePresaleSheetConfig';

interface PresaleSheetEditorProps {
  warrantyUuid: string;
  initialConfig: PresaleSheetConfig;
  /**
   * Style des sections : "page" pour la page dediee (cartes empilees avec
   * fond affinicar-primary/5), "panel" pour le modal du detail garantie
   * (fond gris plus discret).
   */
  variant?: 'page' | 'panel';
}

const sectionClass = (variant: 'page' | 'panel') =>
  variant === 'panel'
    ? 'tw-bg-gray-50 tw-rounded-xl tw-p-4 tw-space-y-3'
    : 'tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-4 sm:tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4 tw-mt-4';

/**
 * Editeur des options de la brochure avant-vente :
 * - toggle showPrice
 * - color picker bannerColor
 * - upload / reset image S3
 *
 * Auto-save debounce via React Query (cache 'presale-sheet-config').
 */
export function PresaleSheetEditor({
  warrantyUuid,
  initialConfig,
  variant = 'page',
}: PresaleSheetEditorProps) {
  const [showPrice, setShowPrice] = useState<boolean>(() => initialConfig.showPrice);
  const [bannerColor, setBannerColor] = useState<string>(() => initialConfig.bannerColor);
  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(() => initialConfig.headerImageUrl);
  const [debouncedBannerColor, setDebouncedBannerColor] = useState<string>(() => initialConfig.bannerColor);

  const upsertMutation = useUpsertPresaleSheetConfig(warrantyUuid);
  const upsertRef = useRef(upsertMutation);
  upsertRef.current = upsertMutation;

  // Debounce du bannerColor (color picker en drag) pour eviter d'envoyer
  // une mutation a chaque pixel et de spammer la regeneration du PDF.
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedBannerColor(bannerColor), 1500);
    return () => clearTimeout(timeout);
  }, [bannerColor]);

  // Auto-save quand showPrice ou debouncedBannerColor change.
  // On skippe le 1er render pour ne pas re-sauver la config qui vient
  // d'etre chargee.
  const isFirstSave = useRef(true);
  useEffect(() => {
    if (isFirstSave.current) {
      isFirstSave.current = false;
      return;
    }
    upsertRef.current.mutate({
      showPrice,
      bannerColor: debouncedBannerColor,
    });
  }, [showPrice, debouncedBannerColor]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadPresaleHeaderImage(warrantyUuid);
  const deleteMutation = useDeletePresaleHeaderImage(warrantyUuid);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMutation.mutate(file, {
      onSuccess: (data) => setHeaderImageUrl(data.headerImageUrl),
    });
  };

  const handleResetImage = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        setHeaderImageUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  };

  const isUploading = uploadMutation.isLoading;
  const previewImage = headerImageUrl ?? DEFAULT_HEADER_IMAGE;
  const hasCustomImage = headerImageUrl !== null;

  return (
    <>
      <div className={sectionClass(variant)}>
        <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
          Choisissez si le tarif doit apparaître sur la brochure
        </p>
        <ToggleInput
          label="Afficher le prix"
          value={showPrice}
          onChange={setShowPrice}
        />
      </div>

      <div className={sectionClass(variant)}>
        <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
          Choisissez la couleur du bandeau et des bordures
        </p>
        <div className="tw-flex tw-items-center tw-gap-3">
          <input
            type="color"
            value={bannerColor}
            onChange={(e) => setBannerColor(e.target.value)}
            className="tw-w-10 tw-h-10 tw-rounded-lg tw-border tw-border-gray-300 tw-cursor-pointer tw-p-0.5"
          />
          <span className="tw-text-sm tw-text-gray-700 tw-font-medium">{bannerColor}</span>
        </div>
      </div>

      <div className={sectionClass(variant)}>
        <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
          Selectionnez l'image de fond du bandeau d'en-tête
        </p>
        <div className="tw-flex tw-items-center tw-gap-3">
          <label className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors tw-text-sm tw-font-medium tw-text-gray-700">
            {isUploading ? (
              <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
            ) : (
              <Upload className="tw-w-4 tw-h-4" />
            )}
            {isUploading ? 'Envoi en cours...' : 'Choisir une image'}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleImageUpload}
              disabled={isUploading}
              className="tw-hidden"
            />
          </label>
          {hasCustomImage && !isUploading && (
            <button
              onClick={handleResetImage}
              className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-sm tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors"
            >
              <RotateCcw className="tw-w-4 tw-h-4" />
              Réinitialiser
            </button>
          )}
        </div>
        {hasCustomImage && (
          <img src={previewImage} alt="Aperçu" className="tw-w-full tw-h-16 tw-object-cover tw-rounded-lg tw-border tw-border-gray-200" />
        )}
        {uploadMutation.isError && (
          <p className="tw-text-xs tw-text-red-500 tw-mb-0">
            Erreur lors de l'upload (fichier trop volumineux ou format non supporté)
          </p>
        )}
      </div>
    </>
  );
}
